// src/features/landing/presentation/pages/JobDetailPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Footer } from "../components/Footer";
import { jobPostRepository } from "../../infrastructure/jobPostApiRepository";
import { jobApplyRepository } from "../../infrastructure/jobApplyApiRepository";
import { termsRepository } from "../../infrastructure/termsApiRepository";
import { siteVisitRepository } from "../../infrastructure/siteVisitApiRepository";
import { useAuthContext } from "../../../auth/application/AuthContext";
import {
    regionLabel,
    strongSalesAreaLabel,
    workTypeLabel,
    employmentTypeLabel,
    supportItemLabel,
} from "../../domain/recruitmentOptions";

import "../styles/landing.css";

const PLANNER_JOB_TYPE = "설계사";

/** jobPost.plannerDetail(JSON 문자열)을 채용직종='설계사'일 때만 파싱해서 반환.
 *  그 외 직무면 admin이 값을 넣지 않아 항상 null이지만, 방어적으로 한 번 더 직무를 확인한다. */
function parsePlannerDetail(jobPost) {
    if (jobPost.jobType !== PLANNER_JOB_TYPE || !jobPost.plannerDetail) return null;
    try {
        const parsed = typeof jobPost.plannerDetail === "string" ? JSON.parse(jobPost.plannerDetail) : jobPost.plannerDetail;
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
}

function formatTime(timeString) {
    if (!timeString) return "";
    // LocalTime format: "HH:mm:ss" or "HH:mm"
    const parts = timeString.split(":");
    if (parts.length >= 2) {
        const hour = parseInt(parts[0], 10);
        const minute = parts[1];
        const period = hour < 12 ? "오전" : "오후";
        const displayHour = hour <= 12 ? hour : hour - 12;
        return `${period} ${displayHour.toString().padStart(2, "0")}:${minute}`;
    }
    return timeString;
}

function getAgeDisplay(ageRangeMin, ageRangeMax) {
    if (!ageRangeMin && !ageRangeMax) return "제한없음";
    if (ageRangeMin && ageRangeMax) return `${ageRangeMin}~${ageRangeMax}세`;
    if (ageRangeMin) return `${ageRangeMin}세 이상`;
    if (ageRangeMax) return `${ageRangeMax}세 이하`;
    return "제한없음";
}

function getSalaryDisplay(min, max) {
    const isMinNegotiable = !min || min === "협의";
    const isMaxNegotiable = !max || max === "협의";

    if (isMinNegotiable && isMaxNegotiable) return "면접후결정";
    if (isMinNegotiable) return `${max} 이하`;
    if (isMaxNegotiable) return `${min} 이상`;
    if (min === max) return min;
    return `${min} ~ ${max}`;
}

export function JobDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { accessToken } = useAuthContext();
    const [jobPost, setJobPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 지원신청 모달 상태
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [applyForm, setApplyForm] = useState({
        applicantName: '',
        applicantContact: '',
        applicantEmail: '',
        applicantZipcode: '',
        applicantRoadAddress: '',
        applicantJibunAddress: '',
        applicantDetailAddress: '',
        applicantSido: '',
        applicantSigungu: '',
        privacyAgreed: false,
    });
    const [applyLoading, setApplyLoading] = useState(false);
    const [applyError, setApplyError] = useState(null);
    const [applySuccess, setApplySuccess] = useState(false);

    // 약관 상태
    const [privacyTerms, setPrivacyTerms] = useState(null);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsLoading, setTermsLoading] = useState(false);

    // 개인정보처리방침 조회
    useEffect(() => {
        const fetchPrivacyTerms = async () => {
            try {
                setTermsLoading(true);
                const terms = await termsRepository.getTermsByType('PRIVACY');
                setPrivacyTerms(terms);
            } catch (err) {
                console.error('개인정보처리방침 조회 실패:', err);
            } finally {
                setTermsLoading(false);
            }
        };

        fetchPrivacyTerms();
    }, []);

    useEffect(() => {
        const fetchJobPost = async () => {
            try {
                setLoading(true);
                const data = await jobPostRepository.getJobPostById(id);
                setJobPost(data);
                siteVisitRepository.recordVisit('JOB_POST', Number(id));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchJobPost();
        }
    }, [id]);

    const handleBackToList = () => {
        navigate("/jobs");
    };

    // 지원신청 폼 핸들러
    const handleApplyFormChange = (e) => {
        const { name, value } = e.target;
        setApplyForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleOpenApplyModal = () => {
        setShowApplyModal(true);
        setApplyError(null);
        setApplySuccess(false);
    };

    const handleCloseApplyModal = () => {
        setShowApplyModal(false);
        setApplyForm({
            applicantName: '',
            applicantContact: '',
            applicantEmail: '',
            applicantZipcode: '',
            applicantRoadAddress: '',
            applicantJibunAddress: '',
            applicantDetailAddress: '',
            applicantSido: '',
            applicantSigungu: '',
            privacyAgreed: false,
        });
        setApplyError(null);
        setApplySuccess(false);
    };

    const handleApplySubmit = async (e) => {
        e.preventDefault();

        if (!applyForm.applicantName.trim()) {
            setApplyError('이름을 입력해주세요.');
            return;
        }
        if (!applyForm.applicantContact.trim()) {
            setApplyError('연락처를 입력해주세요.');
            return;
        }
        if (!applyForm.privacyAgreed) {
            setApplyError('개인정보 수집/이용에 동의해주세요.');
            return;
        }

        setApplyLoading(true);
        setApplyError(null);

        try {
            await jobApplyRepository.createJobApply({
                jobPostId: parseInt(id),
                ...applyForm,
                applicantContact: applyForm.applicantContact.replace(/-/g, ""),
            }, accessToken);
            setApplySuccess(true);
        } catch (err) {
            setApplyError(err.message || '지원 신청에 실패했습니다.');
        } finally {
            setApplyLoading(false);
        }
    };

    // 다음 주소검색 API
    const handleAddressSearch = () => {
        if (!window.daum || !window.daum.Postcode) {
            // 다음 우편번호 스크립트 동적 로드
            const script = document.createElement('script');
            script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
            script.onload = () => openAddressPopup();
            document.head.appendChild(script);
        } else {
            openAddressPopup();
        }
    };

    const openAddressPopup = () => {
        new window.daum.Postcode({
            oncomplete: function(data) {
                // 도로명 주소
                const roadAddress = data.roadAddress;
                // 지번 주소
                const jibunAddress = data.jibunAddress || data.autoJibunAddress;
                // 우편번호
                const zipcode = data.zonecode;
                // 시/도
                const sido = data.sido;
                // 시/군/구
                const sigungu = data.sigungu;

                setApplyForm(prev => ({
                    ...prev,
                    applicantZipcode: zipcode,
                    applicantRoadAddress: roadAddress,
                    applicantJibunAddress: jibunAddress,
                    applicantSido: sido,
                    applicantSigungu: sigungu,
                }));

                // 주소검색 레이어 자동 닫기
                document.getElementById('address-search-layer').style.display = 'none';
            },
            width: '100%',
            height: '100%',
        }).embed(document.getElementById('address-search-wrap'));

        // 주소검색 레이어 표시
        document.getElementById('address-search-layer').style.display = 'flex';
    };

    const closeAddressLayer = () => {
        document.getElementById('address-search-layer').style.display = 'none';
    };

    if (loading) {
        return (
            <>
                <div style={{ paddingTop: '40px', minHeight: '100vh', background: '#f8f9fa' }}>
                    <div className="job-detail-loading">
                        <div className="loading-spinner"></div>
                        <p>채용정보를 불러오는 중...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !jobPost) {
        return (
            <>
                <div style={{ paddingTop: '40px', minHeight: '100vh', background: '#f8f9fa' }}>
                    <div className="job-detail-error">
                        <p>채용정보를 불러오는 데 실패했습니다.</p>
                        <button onClick={handleBackToList} className="back-to-list-btn">
                            채용공고목록
                        </button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const plannerDetail = parsePlannerDetail(jobPost);
    const plannerRegions = plannerDetail ? (plannerDetail.recruitRegions || []).map(regionLabel) : [];
    const plannerSalesAreas = plannerDetail
        ? (plannerDetail.strongSalesAreas || []).map((c) => (c === "ETC" && plannerDetail.strongSalesAreasEtc ? plannerDetail.strongSalesAreasEtc : strongSalesAreaLabel(c)))
        : [];
    const plannerSupports = plannerDetail
        ? (plannerDetail.supportItems || []).map((c) => (c === "ETC" && plannerDetail.supportItemsEtc ? plannerDetail.supportItemsEtc : supportItemLabel(c)))
        : [];
    const plannerConditions = plannerDetail
        ? [
              { label: "정착지원금 또는 교육비", value: plannerDetail.relocationSettlementSupport },
              { label: "근무 형태", value: plannerDetail.relocationWorkType ? workTypeLabel(plannerDetail.relocationWorkType) : null },
              { label: "고용 형태", value: plannerDetail.employmentTypes && plannerDetail.employmentTypes.length > 0 ? plannerDetail.employmentTypes.map(employmentTypeLabel).join(", ") : null },
              { label: "영업 베네핏", value: plannerDetail.salesBenefit },
              { label: "교육 시스템", value: plannerDetail.educationSystem },
              { label: "평균 소득", value: plannerDetail.averageIncome },
              { label: "기본수수료 + 프로모션", value: plannerDetail.baseCommissionPromotion },
          ].filter((c) => c.value != null && c.value !== "")
        : [];
    const hasPlannerSection = plannerRegions.length > 0 || plannerSalesAreas.length > 0 || plannerConditions.length > 0 || plannerSupports.length > 0;

    return (
        <>
            <div style={{ paddingTop: '40px', minHeight: '100vh', background: '#f8f9fa' }}>
                <div className="job-detail-container">

                    {/* Header Section */}
                    <section className="job-detail-header">
                        <h1 className="job-detail-title">
                            {jobPost.companyName} {jobPost.jobType || "채용"} 채용공고
                        </h1>
                    </section>

                    {/* Company Info Section */}
                    <section className="job-detail-company">
                        <div className="company-info-grid">
                            <div className="company-info-item">
                                <span className="info-label">회사명</span>
                                <span className="info-value">{jobPost.companyName}</span>
                            </div>
                            <div className="company-info-item">
                                <span className="info-label">회사위치</span>
                                <span className="info-value">
                                    {jobPost.companySido && jobPost.companySigungu
                                        ? `${jobPost.companySido} ${jobPost.companySigungu}`
                                        : "-"}
                                </span>
                            </div>
                        </div>
                    </section>

                    {/* Condition Cards Section */}
                    <section className="job-detail-conditions">
                        {/* 근무조건 */}
                        <div className="condition-card">
                            <div className="condition-header">
                                <span className="condition-icon">🏢</span>
                                <h3>근무조건</h3>
                            </div>
                            <div className="condition-content">
                                <div className="condition-row">
                                    <span className="condition-label">급여</span>
                                    <span
                                        className="condition-value">{getSalaryDisplay(jobPost.salaryRangeMin, jobPost.salaryRangeMax)}</span>
                                </div>
                                <div className="condition-row">
                                    <span className="condition-label">근무요일</span>
                                    <span className="condition-value">{jobPost.workDays || "-"}</span>
                                </div>
                                <div className="condition-row">
                                    <span className="condition-label">근무시간</span>
                                    <span className="condition-value">
                                        {jobPost.workStartTime && jobPost.workEndTime
                                            ? `${formatTime(jobPost.workStartTime)} ~ ${formatTime(jobPost.workEndTime)}`
                                            : "-"}
                                    </span>
                                </div>
                                <div className="condition-row">
                                    <span className="condition-label">채용직종</span>
                                    <span className="condition-value">{jobPost.jobType || "-"}</span>
                                </div>
                                <div className="condition-row">
                                    <span className="condition-label">복리후생</span>
                                    <span className="condition-value">{jobPost.welfare || "-"}</span>
                                </div>
                            </div>
                        </div>

                        {/* 자격조건 */}
                        <div className="condition-card">
                            <div className="condition-header">
                                <span className="condition-icon">👤</span>
                                <h3>자격조건</h3>
                            </div>
                            <div className="condition-content">
                                <div className="condition-row">
                                    <span className="condition-label">경력</span>
                                    <span className="condition-value">{jobPost.career || "무관"}</span>
                                </div>
                                <div className="condition-row">
                                    <span className="condition-label">학력</span>
                                    <span className="condition-value">{jobPost.education || "무관"}</span>
                                </div>
                                <div className="condition-row">
                                    <span className="condition-label">성별</span>
                                    <span className="condition-value">{jobPost.gender || "무관"}</span>
                                </div>
                                <div className="condition-row">
                                    <span className="condition-label">나이</span>
                                    <span
                                        className="condition-value">{getAgeDisplay(jobPost.ageRangeMin, jobPost.ageRangeMax)}</span>
                                </div>
                            </div>
                        </div>

                        {/* 접수기간/방법 */}
                        <div className="condition-card">
                            <div className="condition-header">
                                <span className="condition-icon">📋</span>
                                <h3>접수기간 / 방법</h3>
                            </div>
                            <div className="condition-content">
                                <div className="condition-row">
                                    <span className="condition-label">접수기간</span>
                                    <span className="condition-value">
                                        채용시까지
                                    </span>
                                </div>
                                <div className="condition-row">
                                    <span className="condition-label">접수방법</span>
                                    <span className="condition-value">온라인 신청</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* 설계사 채용관 전용 정보 — 채용직종='설계사'일 때만 노출 */}
                    {hasPlannerSection && (
                        <section className="job-detail-planner-section">
                            {plannerRegions.length > 0 && (
                                <div className="planner-block">
                                    <h3 className="section-title">채용 지역</h3>
                                    <div className="planner-tag-row">
                                        {plannerRegions.map((r, i) => <span key={i} className="planner-tag">{r}</span>)}
                                    </div>
                                </div>
                            )}
                            {plannerSalesAreas.length > 0 && (
                                <div className="planner-block">
                                    <h3 className="section-title">강점 영업 영역</h3>
                                    <div className="planner-tag-row">
                                        {plannerSalesAreas.map((a, i) => <span key={i} className="planner-tag">{a}</span>)}
                                    </div>
                                </div>
                            )}
                            {plannerConditions.length > 0 && (
                                <div className="planner-block">
                                    <h3 className="section-title">이직 시 제공 조건</h3>
                                    <div className="planner-condition-grid">
                                        {plannerConditions.map((c, i) => (
                                            <div key={i} className="planner-condition-card">
                                                <div className="planner-condition-label">{c.label}</div>
                                                <div className="planner-condition-value">{c.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {plannerSupports.length > 0 && (
                                <div className="planner-block">
                                    <h3 className="section-title">지원 사항</h3>
                                    <div className="planner-tag-row">
                                        {plannerSupports.map((s, i) => <span key={i} className="planner-tag">{s}</span>)}
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {/* Request Note Section */}
                    {jobPost.requestNote && (
                        <section className="job-detail-note-section">
                            <h3 className="section-title">채용 상세 정보</h3>
                            <div className="note-container">
                                <ul className="note-checklist">
                                    {jobPost.requestNote.split(',').map((item, index) => (
                                        item.trim() && (
                                            <li key={index} className="checklist-item">
                                                <span className="checklist-icon"></span>
                                                <span className="checklist-text">{item.trim()}</span>
                                            </li>
                                        )
                                    ))}
                                </ul>
                            </div>
                        </section>
                    )}

                    <section>
                        <div className="premium-badge">
                            <p className="premium-text">
                                <span className="premium-check"></span> 본 공고는 라이징브로커즈 검증을 완료한 <strong>프리미엄
                                채용공고</strong>입니다.
                            </p>
                            <p className="premium-subtext">일반 지원과 달리, 라이징브로커즈 전담 매니저가 1:1로 끝까지 함께합니다.</p>
                        </div>
                        {/* Bottom CTA Section */}
                        <div className="job-detail-actions">
                            <button className="bottom-apply-btn" onClick={handleOpenApplyModal}>
                                프리미엄 공고 지원 신청하기
                            </button>
                        </div>
                    </section>

                    {/* Premium Service Info Section */}
                    <section className="premium-service-section">
                        <div className="premium-service-header">
                            <h3>라이징브로커즈 검증 채용공고</h3>
                        </div>
                        <div className="premium-service-content">
                            <p className="premium-service-intro">라이징브로커즈가 아래 절차에 따라<br/><strong>직접 검증한 공고</strong>입니다.
                            </p>
                            <ul className="premium-service-list">
                                <li><span className="check-icon">✔</span> 실제 근무 조건 확인 완료</li>
                                <li><span className="check-icon">✔</span> 허위·과장 여부 검증 완료</li>
                                <li><span className="check-icon">✔</span> 지원 후 1:1 전담 케어</li>
                            </ul>
                        </div>
                    </section>

                    <section>
                        <div className="job-detail-actions">
                            <button onClick={handleBackToList} className="back-to-list-btn">
                                공고 목록 보기
                            </button>
                        </div>
                    </section>
                </div>
            </div>
            <Footer/>

            {/* 지원신청 모달 */}
            {showApplyModal && (
                <div className="apply-modal-overlay" onClick={handleCloseApplyModal}>
                    <div className="apply-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="apply-modal-header">
                            <h2>지원신청</h2>
                            <button className="modal-close-btn" onClick={handleCloseApplyModal}>&times;</button>
                        </div>

                        {applySuccess ? (
                            <div className="apply-success">
                                <div className="success-icon">&#10003;</div>
                                <h3>지원이 완료되었습니다!</h3>
                                <p>담당자가 확인 후 연락드리겠습니다.</p>
                                <button className="apply-close-btn" onClick={handleCloseApplyModal}>
                                    닫기
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleApplySubmit} className="apply-form">
                                <div className="form-group">
                                    <label className="form-label required">이름</label>
                                    <input
                                        type="text"
                                        name="applicantName"
                                        value={applyForm.applicantName}
                                        onChange={handleApplyFormChange}
                                        className="form-input"
                                        placeholder="이름을 입력해주세요"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label required">연락처</label>
                                    <input
                                        type="tel"
                                        name="applicantContact"
                                        value={applyForm.applicantContact}
                                        onChange={handleApplyFormChange}
                                        className="form-input"
                                        placeholder="010-0000-0000"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">이메일</label>
                                    <input
                                        type="email"
                                        name="applicantEmail"
                                        value={applyForm.applicantEmail}
                                        onChange={handleApplyFormChange}
                                        className="form-input"
                                        placeholder="risingbrokers@gmail.com"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">주소</label>
                                    <div className="address-search-row">
                                        <input
                                            type="text"
                                            name="applicantZipcode"
                                            value={applyForm.applicantZipcode}
                                            readOnly
                                            className="form-input zipcode-input"
                                            placeholder="우편번호"
                                        />
                                        <button
                                            type="button"
                                            className="address-search-btn"
                                            onClick={handleAddressSearch}
                                        >
                                            주소검색
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        name="applicantRoadAddress"
                                        value={applyForm.applicantRoadAddress}
                                        readOnly
                                        className="form-input"
                                        placeholder="도로명 주소"
                                        style={{ marginTop: '8px' }}
                                    />
                                    {applyForm.applicantJibunAddress && (
                                        <input
                                            type="text"
                                            name="applicantJibunAddress"
                                            value={applyForm.applicantJibunAddress}
                                            readOnly
                                            className="form-input jibun-input"
                                            placeholder="지번 주소"
                                            style={{ marginTop: '8px' }}
                                        />
                                    )}
                                    <input
                                        type="text"
                                        name="applicantDetailAddress"
                                        value={applyForm.applicantDetailAddress}
                                        onChange={handleApplyFormChange}
                                        className="form-input"
                                        placeholder="상세 주소를 입력해주세요"
                                        style={{ marginTop: '8px' }}
                                    />
                                </div>

                                {/* 동의 체크박스 */}
                                <div className="form-group consent-group">
                                    <label className="form-label required">개인정보 수집/이용 동의</label>
                                    <div className="consent-item">
                                        <span className="consent-inline">
                                            <span className="consent-text">[필수] 개인정보 수집/이용에 동의합니다.</span>
                                            <label className="consent-label">
                                                <input
                                                    type="checkbox"
                                                    name="privacyAgreed"
                                                    checked={applyForm.privacyAgreed}
                                                    onChange={(e) => setApplyForm(prev => ({
                                                        ...prev,
                                                        privacyAgreed: e.target.checked
                                                    }))}
                                                    className="consent-checkbox"
                                                />
                                            </label>
                                            <button
                                                type="button"
                                                className="terms-view-btn"
                                                onClick={() => setShowTermsModal(true)}
                                            >
                                                내용보기
                                            </button>
                                        </span>
                                    </div>
                                </div>

                                {applyError && (
                                    <div className="apply-error">{applyError}</div>
                                )}

                                <div className="apply-form-actions">
                                    <button
                                        type="button"
                                        className="apply-cancel-btn"
                                        onClick={handleCloseApplyModal}
                                    >
                                        취소
                                    </button>
                                    <button
                                        type="submit"
                                        className="apply-submit-btn"
                                        disabled={applyLoading}
                                    >
                                        {applyLoading ? '신청 중...' : '지원하기'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* 주소검색 레이어 */}
            <div id="address-search-layer" className="address-search-layer">
                <div className="address-search-content">
                    <div className="address-search-header">
                        <h3>주소검색</h3>
                        <button type="button" className="address-close-btn" onClick={closeAddressLayer}>&times;</button>
                    </div>
                    <div id="address-search-wrap" className="address-search-wrap"></div>
                </div>
            </div>

            {/* 개인정보처리방침 모달 */}
            {showTermsModal && (
                <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
                    <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="terms-modal-header">
                            <h2>{privacyTerms?.title || '개인정보 수집/이용 동의'}</h2>
                            <button className="modal-close-btn" onClick={() => setShowTermsModal(false)}>&times;</button>
                        </div>
                        <div className="terms-modal-body">
                            {termsLoading ? (
                                <div className="terms-loading">
                                    <div className="loading-spinner"></div>
                                    <p>약관을 불러오는 중...</p>
                                </div>
                            ) : privacyTerms ? (
                                <div className="terms-content" dangerouslySetInnerHTML={{ __html: privacyTerms.content.replace(/\n/g, '<br/>') }} />
                            ) : (
                                <div className="terms-content">
                                    <p><strong>1. 수집하는 개인정보 항목</strong></p>
                                    <p>필수: 이름, 연락처, 이메일, 주소</p>
                                    <p><strong>2. 개인정보의 수집 및 이용 목적</strong></p>
                                    <p>채용 지원 접수 및 결과 안내</p>
                                    <p><strong>3. 개인정보의 보유 및 이용 기간</strong></p>
                                    <p>지원일로부터 1년간 보관 후 파기</p>
                                </div>
                            )}
                        </div>
                        <div className="terms-modal-footer">
                            {privacyTerms && (
                                <span className="terms-version">버전: {privacyTerms.version}</span>
                            )}
                            <button className="terms-close-btn" onClick={() => setShowTermsModal(false)}>
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .job-detail-container {
                    max-width: 900px;
                    margin: 0 auto;
                    padding: 40px 20px;
                }

                .job-detail-loading,
                .job-detail-error {
                    text-align: center;
                    padding: 100px 20px;
                    color: #666;
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 3px solid #e9ecef;
                    border-top-color: #f16102;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 16px;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                /* Header */
                .job-detail-header {
                    background: white;
                    padding: 30px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    border: 1px solid #e9ecef;
                }

                .job-detail-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #2c3e50;
                    margin: 0;
                }

                .premium-badge {
                    padding: 14px 18px;
                    background: linear-gradient(135deg, #fff8f0 0%, #fff3e6 100%);
                    border: 1px solid #fc640c;
                    border-radius: 8px;
                }

                .premium-text {
                    margin: 0;
                    font-size: 0.95rem;
                    color: #333;
                    line-height: 1.5;
                }

                .premium-check {
                    display: inline-block;
                    width: 18px;
                    height: 18px;
                    background: linear-gradient(135deg, #fff8ec 0%, #fcf6ef 100%);
                    border-radius: 50%;
                    position: relative;
                    vertical-align: middle;
                }

                .premium-check::after {
                    content: '';
                    position: absolute;
                    left: 45%;
                    top: 30%;
                    width: 8px;
                    height: 12px;
                    border: solid #f16102;
                    border-width: 0 3px 3px 0;
                    transform: translate(-50%, -50%) rotate(44deg);
                }

                .premium-text strong {
                    color: #f16102;
                }

                .premium-subtext {
                    margin: 6px 0 0 0;
                    font-size: 0.9rem;
                    color: #666;
                }

                /* Company Info */
                .job-detail-company {
                    background: white;
                    padding: 30px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    border: 1px solid #e9ecef;
                    display: flex;
                    gap: 30px;
                    align-items: flex-start;
                }

                .company-info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 15px 40px;
                    flex: 1;
                }

                .company-info-item {
                    display: flex;
                    gap: 20px;
                }

                .info-label {
                    margin-top: 2px;
                    color: #888;
                    font-size: 0.9rem;
                    min-width: 60px;
                }

                .info-value {
                    text-align: left;
                    color: #333;
                    font-size: 1.0rem;
                    font-weight: 500;
                }

                /* Condition Cards */
                .job-detail-conditions {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 20px;
                    margin-bottom: 20px;
                }

                .condition-card {
                    background: white;
                    border-radius: 8px;
                    border: 1px solid #e9ecef;
                    overflow: hidden;
                }

                .condition-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 15px 20px;
                    border-bottom: 1px solid #e9ecef;
                    background: #fafafa;
                }

                .condition-icon {
                    font-size: 1.2rem;
                }

                .condition-header h3 {
                    margin: 0;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #333;
                }

                .condition-content {
                    padding: 15px 20px;
                }

                .condition-row {
                    display: flex;
                    gap: 15px;
                    padding: 8px 0;
                    border-bottom: 1px solid #f5f5f5;
                }

                .condition-row:last-child {
                    border-bottom: none;
                }

                .condition-label {
                    color: #888;
                    font-size: 0.85rem;
                    min-width: 60px;
                }

                .condition-value {
                    color: #333;
                    font-size: 0.85rem;
                    flex: 1;
                    word-break: break-word;
                }

                /* Apply Button Section */
                .job-apply-section {
                    border-radius: 8px;
                    padding: 10px 20px;
                    margin-bottom: 20px;
                    text-align: center;
                }

                .apply-description {
                    color: #666;
                    font-size: 0.95rem;
                    margin: 0 0 20px 0;
                }

                .apply-btn {
                    background: linear-gradient(135deg, #f16102 0%, #f16102 100%);
                    color: white;
                    border: none;
                    padding: 15px 60px;
                    font-size: 1.1rem;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    margin: 5px 0 25px 0;
                }

                .apply-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(30, 95, 255, 0.3);
                }

                /* Apply Modal */
                .apply-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    animation: fadeIn 0.2s ease;
                    backdrop-filter: blur(4px);
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .apply-modal {
                    background: white;
                    animation: slideUp 0.3s ease;
                    border-radius: 12px;
                    width: 100%;
                    max-width: 500px;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
                }

                .apply-modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px 24px;
                    border-bottom: 1px solid #e9ecef;
                }

                .apply-modal-header h2 {
                    margin: 0;
                    font-size: 1.3rem;
                    color: #333;
                }

                .modal-close-btn {
                    background: none;
                    border: none;
                    font-size: 1.8rem;
                    color: #999;
                    cursor: pointer;
                    line-height: 1;
                }

                .modal-close-btn:hover {
                    color: #333;
                }

                .apply-form {
                    padding: 24px;
                    overflow-x: hidden;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .consent-group {
                    background: #f8f9fa;
                    padding: 16px;
                    border-radius: 8px;
                    border: 1px solid #e9ecef;
                }

                .consent-item {
                    margin-bottom: 12px;
                    text-align: left;
                }

                .consent-item:last-child {
                    margin-bottom: 0;
                }

                .consent-inline {
                    display: inline-flex;
                }

                .consent-label {
                    display: inline;
                    cursor: pointer;
                }

                .consent-checkbox {
                    width: 16px;
                    height: 16px;
                    accent-color: #f16102;
                    cursor: pointer;
                    vertical-align: middle;
                    margin: 0 5px;
                }

                .consent-text {
                    font-size: 0.9rem;
                    color: #333;
                    font-weight: 500;
                }

                .consent-detail {
                    font-size: 0.8rem;
                    color: #888;
                    margin: 6px 0 0 28px;
                    line-height: 1.4;
                }

                .terms-view-btn {
                    background: none;
                    border: none;
                    color: #f16102;
                    font-size: 0.85rem;
                    cursor: pointer;
                    text-decoration: underline;
                    padding-left: 10px;
                    vertical-align: middle;
                }

                .terms-view-btn:hover {
                    color: #d55502;
                }

                /* Terms Modal */
                .terms-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2000;
                    padding: 20px;
                    animation: fadeIn 0.2s ease;
                    backdrop-filter: blur(4px);
                }

                .terms-modal {
                    background: white;
                    border-radius: 12px;
                    width: 100%;
                    max-width: 600px;
                    max-height: 80vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
                    animation: slideUp 0.3s ease;
                }

                .terms-modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px 24px;
                    border-bottom: 1px solid #e9ecef;
                    flex-shrink: 0;
                }

                .terms-modal-header h2 {
                    margin: 0;
                    font-size: 1.2rem;
                    color: #333;
                }

                .terms-modal-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 24px;
                }

                .terms-loading {
                    text-align: center;
                    padding: 40px;
                    color: #666;
                }

                .terms-content {
                    font-size: 0.9rem;
                    color: #333;
                    line-height: 1.7;
                    text-align: left;
                }

                .terms-content p {
                    margin: 0 0 12px 0;
                }

                .terms-modal-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 24px;
                    border-top: 1px solid #e9ecef;
                    flex-shrink: 0;
                }

                .terms-version {
                    font-size: 0.8rem;
                    color: #888;
                }

                .terms-close-btn {
                    padding: 10px 24px;
                    background: #f16102;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-size: 0.95rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .terms-close-btn:hover {
                    background: #d55502;
                }

                .form-label {
                    display: block;
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #333;
                    margin-bottom: 8px;
                    text-align: left;
                }

                .form-label.required::after {
                    content: ' *';
                    color: #e53935;
                }

                .form-input {
                    width: 100%;
                    padding: 12px 14px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 0.95rem;
                    transition: border-color 0.2s;
                    box-sizing: border-box;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #f16102;
                }

                .form-input[readonly] {
                    background: #f8f9fa;
                    cursor: default;
                }

                .address-search-row {
                    display: flex;
                    gap: 8px;
                    width: 100%;
                }

                .zipcode-input {
                    flex: 1;
                    min-width: 0;
                }

                .address-search-btn {
                    padding: 12px 14px;
                    background: #666;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: background 0.2s;
                    flex-shrink: 0;
                }

                .address-search-btn:hover {
                    background: #555;
                }

                .jibun-input {
                    color: #888;
                    font-size: 0.9rem;
                }

                /* Address Search Layer */
                .address-search-layer {
                    display: none;
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.6);
                    z-index: 2000;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                }

                .address-search-content {
                    background: white;
                    border-radius: 12px;
                    width: 100%;
                    max-width: 500px;
                    height: 500px;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                .address-search-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 20px;
                    border-bottom: 1px solid #e9ecef;
                    flex-shrink: 0;
                }

                .address-search-header h3 {
                    margin: 0;
                    font-size: 1.1rem;
                    color: #333;
                }

                .address-close-btn {
                    background: none;
                    border: none;
                    font-size: 1.8rem;
                    color: #999;
                    cursor: pointer;
                    line-height: 1;
                }

                .address-close-btn:hover {
                    color: #333;
                }

                .address-search-wrap {
                    flex: 1;
                    overflow: hidden;
                }

                .apply-error {
                    background: #ffebee;
                    color: #c62828;
                    padding: 12px;
                    border-radius: 6px;
                    font-size: 0.9rem;
                    margin-bottom: 20px;
                }

                .apply-form-actions {
                    display: flex;
                    gap: 12px;
                    margin-top: 24px;
                }

                .apply-cancel-btn {
                    flex: 1;
                    padding: 14px;
                    border: 1px solid #e5e7eb;
                    background: #f9fafb;
                    color: #4b5563;
                    font-size: 1rem;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .apply-cancel-btn:hover {
                    background: #f3f4f6;
                    border-color: #d1d5db;
                }

                .apply-submit-btn {
                    flex: 1;
                    padding: 14px;
                    border: none;
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                    color: white;
                    font-size: 1rem;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.2);
                }

                .apply-submit-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.35);
                }

                .apply-submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .apply-success {
                    padding: 50px 24px;
                    text-align: center;
                    animation: fadeInUp 0.4s ease;
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .success-icon {
                    width: 40px;
                    height: 40px;
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                    color: white;
                    margin: 0 auto 24px;
                    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
                    animation: scaleIn 0.5s ease;
                }

                @keyframes scaleIn {
                    0% {
                        transform: scale(0);
                    }
                    50% {
                        transform: scale(1.1);
                    }
                    100% {
                        transform: scale(1);
                    }
                }

                .apply-success h3 {
                    margin: 0 0 12px 0;
                    font-size: 1.4rem;
                    font-weight: 700;
                    color: #1f2937;
                }

                .apply-success p {
                    margin: 0 0 28px 0;
                    color: #6b7280;
                    font-size: 0.95rem;
                    line-height: 1.5;
                }

                .apply-close-btn {
                    padding: 14px 48px;
                    border: none;
                    background: linear-gradient(135deg, #f16102 0%, #f16102 100%);
                    color: white;
                    font-size: 1rem;
                    font-weight: 600;
                    border-radius: 10px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.3);
                }

                .apply-close-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
                }

                .section-title {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #333;
                    text-align: left;
                    margin: 0;
                    padding: 20px;
                    border-bottom: 1px solid #e9ecef;
                }
                
                /* Premium Service Section */
                .premium-service-section {
                    background: white;
                    border-radius: 8px;
                    border: 1px solid #e9ecef;
                    margin-bottom: 20px;
                    overflow: hidden;
                }

                .premium-service-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 15px 20px;
                    border-bottom: 1px solid #e9ecef;
                    background: #fafafa;
                }

                .premium-service-icon {
                    font-size: 1.2rem;
                }

                .premium-service-header h3 {
                    margin: 0;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #333;
                }

                .premium-service-content {
                    padding: 20px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                }

                .premium-service-intro {
                    margin: 0 0 16px 0;
                    color: #555;
                    font-size: 0.95rem;
                    line-height: 1.6;
                }

                .premium-service-intro strong {
                    color: #f16102;
                }

                .premium-service-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: inline-flex;
                    flex-direction: column;
                    align-items: flex-start;
                }

                .premium-service-list li {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 0;
                    color: #333;
                    font-size: 0.95rem;
                }

                .premium-service-list .check-icon {
                    color: #f16102;
                    font-weight: 700;
                }

                .premium-service-outro {
                    margin: 0;
                    color: #555;
                    font-size: 0.95rem;
                }

                .premium-service-outro strong {
                    color: #f16102;
                }

                /* Planner Section (설계사 채용관 전용 정보) */
                .job-detail-planner-section {
                    background: white;
                    border-radius: 8px;
                    border: 1px solid #e9ecef;
                    margin-bottom: 20px;
                    padding: 25px;
                    display: flex;
                    flex-direction: column;
                    gap: 40px;
                }

                .planner-block:last-child { margin-bottom: 0; }

                .planner-block > .section-title {
                    font-size: 1rem;
                    padding: 0 0 14px;
                    margin-bottom: 14px;
                }

                .planner-tag-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                }

                .planner-tag {
                    padding: 7px 16px;
                    background: #fff3e6;
                    color: #f16102;
                    border: 1.5px solid #f7b57a;
                    border-radius: 999px;
                    font-size: 0.85rem;
                    font-weight: 600;
                }

                .planner-condition-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                }

                .planner-condition-card {
                    background: #fafafa;
                    border: 1px solid #f0f0f0;
                    border-radius: 8px;
                    padding: 12px 14px;
                }

                .planner-condition-label {
                    font-size: 0.85rem;
                    color: #888;
                    font-weight: 600;
                    margin-bottom: 4px;
                }

                .planner-condition-value {
                    font-size: 0.85rem;
                    color: #333;
                    line-height: 1.5;
                    word-break: break-word;
                }

                /* Note Section */
                .job-detail-note-section {
                    background: white;
                    border-radius: 8px;
                    border: 1px solid #e9ecef;
                    margin-bottom: 30px;
                    overflow: hidden;
                }

                .note-container {
                    padding: 25px;
                }

                .note-title {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #333;
                    margin: 0 0 20px 0;
                }

                .note-content {
                    color: #555;
                    font-size: 0.95rem;
                    line-height: 1.8;
                }

                .note-content p {
                    margin: 0 0 8px 0;
                }

                .note-checklist {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                    align-items: center;
                }

                .checklist-item {
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    gap: 10px;
                    padding: 10px 0;
                }

                .checklist-icon {
                    flex-shrink: 0;
                    width: 20px;
                    height: 20px;
                    background: linear-gradient(135deg, #fff8ec 0%, #fcf6ef 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                }

                .checklist-icon::after {
                    content: '';
                    width: 4px;
                    height: 9px;
                    border: solid #f16102;
                    border-width: 0 2.5px 2.5px 0;
                    transform: rotate(40deg);
                    margin-top: -2px;
                }

                .checklist-text {
                    color: #333;
                    font-size: 0.9rem;
                    line-height: 1.4;
                }

                /* Actions */
                .job-detail-actions {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                    padding: 30px 0;
                }

                .bottom-apply-btn {
                    width: 100%;
                    max-width: 400px;
                    padding: 16px 40px;
                    background: linear-gradient(135deg, #f16102 0%, #e55a00 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 1.1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(241, 97, 2, 0.3);
                }

                .bottom-apply-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(241, 97, 2, 0.4);
                }

                .back-to-list-btn {
                    width: 100%;
                    max-width: 400px;
                    padding: 14px 40px;
                    background: white;
                    color: #5a6a7a;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    font-size: 1rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .back-to-list-btn:hover {
                    background: #f9fafb;
                    border-color: #9ca3af;
                }

                /* Responsive */
                @media (max-width: 900px) {
                    .job-detail-conditions {
                        grid-template-columns: 1fr;
                    }

                    .company-info-grid {
                        grid-template-columns: 1fr;
                    }
                }

                @media (max-width: 600px) {
                    .job-detail-container {
                        padding: 16px;
                    }

                    .job-detail-header {
                        padding: 20px 16px;
                        margin-bottom: 16px;
                    }

                    .job-detail-title {
                        font-size: 1.1rem;
                        line-height: 1.4;
                    }

                    .premium-badge {
                        margin-top: 12px;
                        padding: 12px 40px 14px 40px;
                    }

                    .premium-text {
                        font-size: 0.85rem;
                    }

                    .premium-subtext {
                        font-size: 0.8rem;
                    }

                    .job-detail-company {
                        flex-direction: column;
                        align-items: flex-start;
                        padding: 20px 16px;
                        margin-bottom: 16px;
                        gap: 20px;
                    }

                    .company-info-grid {
                        gap: 12px;
                        width: 100%;
                    }

                    .company-info-item {
                        flex-direction: row;
                        gap: 20px;
                        text-align: left;
                    }

                    .info-label {
                        font-size: 0.8rem;
                    }

                    .info-value {
                        font-size: 0.85rem;
                    }

                    .job-detail-conditions {
                        gap: 16px;
                        margin-bottom: 16px;
                    }

                    .condition-header {
                        padding: 12px 16px;
                    }

                    .condition-header h3 {
                        font-size: 0.95rem;
                    }

                    .condition-content {
                        padding: 12px 16px;
                    }

                    .condition-row {
                        padding: 6px 0;
                    }

                    .condition-label {
                        font-size: 0.8rem;
                        min-width: 50px;
                    }

                    .condition-value {
                        font-size: 0.8rem;
                    }

                    .job-apply-section {
                        padding: 16px;
                        margin-bottom: 16px;
                    }

                    .apply-btn {
                        width: 100%;
                        padding: 14px;
                        font-size: 1rem;
                    }

                    .apply-modal {
                        margin: 10px;
                        max-height: 95vh;
                    }

                    .apply-modal-header {
                        padding: 16px;
                    }

                    .apply-modal-header h2 {
                        font-size: 1.1rem;
                    }

                    .apply-form {
                        padding: 16px;
                    }

                    .form-group {
                        margin-bottom: 16px;
                    }

                    .form-label {
                        font-size: 0.85rem;
                    }

                    .form-input {
                        padding: 10px 12px;
                        font-size: 0.9rem;
                    }

                    .address-search-row {
                        flex-direction: row;
                    }

                    .zipcode-input {
                        flex: 1;
                    }

                    .address-search-btn {
                        padding: 10px 12px;
                        font-size: 0.8rem;
                    }

                    .address-search-content {
                        height: 450px;
                        max-width: 100%;
                    }

                    .apply-form-actions {
                        flex-direction: column;
                    }

                    .apply-success {
                        padding: 30px 16px;
                    }

                    .job-detail-note-section {
                        margin-bottom: 20px;
                    }

                    .job-detail-planner-section {
                        padding: 16px;
                        margin-bottom: 16px;
                        gap: 28px;
                    }

                    .planner-condition-grid {
                        grid-template-columns: 1fr;
                    }

                    .note-container {
                        padding: 16px;
                    }

                    .note-title {
                        font-size: 1rem;
                        margin-bottom: 16px;
                    }

                    .note-content {
                        font-size: 0.9rem;
                        line-height: 1.7;
                    }

                    .note-checklist {
                        grid-template-columns: 1fr;
                        gap: 4px;
                    }

                    .checklist-item {
                        padding: 8px 0;
                        justify-content: flex-start;
                    }

                    .checklist-icon {
                        width: 22px;
                        height: 22px;
                    }

                    .checklist-icon::after {
                        width: 6px;
                        height: 9px;
                        border-width: 0 2.5px 2.5px 0;
                    }

                    .checklist-text {
                        font-size: 0.8rem;
                    }

                    /* Premium Service Section - Mobile */
                    .premium-service-section {
                        margin: 16px 0 16px 0;
                    }

                    .premium-service-header {
                        padding: 12px 16px;
                    }

                    .premium-service-header h3 {
                        font-size: 0.9rem;
                    }

                    .premium-service-content {
                        padding: 16px;
                    }

                    .premium-service-intro,
                    .premium-service-outro,
                    .premium-service-list li {
                        font-size: 0.85rem;
                    }

                    /* Actions - Mobile */
                    .job-detail-actions {
                        padding: 20px 16px;
                    }

                    .bottom-apply-btn {
                        padding: 14px 30px;
                        font-size: 1rem;
                    }

                    .back-to-list-btn {
                        padding: 12px 30px;
                        font-size: 0.9rem;
                    }

                    /* Terms Modal - Mobile */
                    .terms-modal {
                        max-width: 100%;
                        max-height: 90vh;
                        margin: 10px;
                    }

                    .terms-modal-header {
                        padding: 16px;
                    }

                    .terms-modal-header h2 {
                        font-size: 1.05rem;
                    }

                    .terms-modal-body {
                        padding: 16px;
                    }

                    .terms-content {
                        font-size: 0.85rem;
                    }

                    .terms-modal-footer {
                        padding: 12px 16px;
                        flex-direction: column;
                        gap: 10px;
                    }

                    .terms-close-btn {
                        width: 100%;
                    }
                }
            `}</style>
        </>
    );
}
